import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  Laptop, Users, FileText, Globe, Euro, Percent,
  ArrowRight, CheckCircle, Zap, Shield,
  Clock, Loader2, LogOut,
  Sun, Moon, BarChart2, Lock, Star,
  TrendingUp, Building2, Sparkles, Menu, X,
} from 'lucide-react';
import { fetchAllInvoiceTypes, getInvoiceStats } from '../services/api';
import { useEntitlements, Features } from '../hooks/useEntitlements';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getAllInvoiceTypes as getLocalInvoiceTypes } from '../config/invoiceTypes';
import UpgradeModal from '../components/UpgradeModal';

const iconMap = { Laptop, Users, FileText, Globe, Euro, Percent };

const typeConfig = {
  freelancer: {
    gradient: 'from-blue-500 to-blue-600',
    light: 'border-blue-100 dark:border-blue-800/40',
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300',
    btn: 'bg-blue-600 hover:bg-blue-700 shadow-blue-600/20',
    icon: 'text-blue-600 dark:text-blue-400',
    iconBg: 'bg-blue-50 dark:bg-blue-900/40',
    dot: 'bg-blue-500',
    ring: 'hover:ring-blue-200 dark:hover:ring-blue-800',
  },
  coach: {
    gradient: 'from-violet-500 to-purple-600',
    light: 'border-violet-100 dark:border-violet-800/40',
    badge: 'bg-violet-100 text-violet-700 dark:bg-violet-900/60 dark:text-violet-300',
    btn: 'bg-violet-600 hover:bg-violet-700 shadow-violet-600/20',
    icon: 'text-violet-600 dark:text-violet-400',
    iconBg: 'bg-violet-50 dark:bg-violet-900/40',
    dot: 'bg-violet-500',
    ring: 'hover:ring-violet-200 dark:hover:ring-violet-800',
  },
  gst: {
    gradient: 'from-emerald-500 to-teal-600',
    light: 'border-emerald-100 dark:border-emerald-800/40',
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300',
    btn: 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-600/20',
    icon: 'text-emerald-600 dark:text-emerald-400',
    iconBg: 'bg-emerald-50 dark:bg-emerald-900/40',
    dot: 'bg-emerald-500',
    ring: 'hover:ring-emerald-200 dark:hover:ring-emerald-800',
  },
  vat: {
    gradient: 'from-orange-500 to-amber-500',
    light: 'border-orange-100 dark:border-orange-800/40',
    badge: 'bg-orange-100 text-orange-700 dark:bg-orange-900/60 dark:text-orange-300',
    btn: 'bg-orange-500 hover:bg-orange-600 shadow-orange-500/20',
    icon: 'text-orange-600 dark:text-orange-400',
    iconBg: 'bg-orange-50 dark:bg-orange-900/40',
    dot: 'bg-orange-500',
    ring: 'hover:ring-orange-200 dark:hover:ring-orange-800',
  },
  international: {
    gradient: 'from-indigo-500 to-blue-600',
    light: 'border-indigo-100 dark:border-indigo-800/40',
    badge: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-300',
    btn: 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-600/20',
    icon: 'text-indigo-600 dark:text-indigo-400',
    iconBg: 'bg-indigo-50 dark:bg-indigo-900/40',
    dot: 'bg-indigo-500',
    ring: 'hover:ring-indigo-200 dark:hover:ring-indigo-800',
  },
};

// ─── Nav theme toggle ─────────────────────────────────────────────────────────

function ThemeToggle() {
  const { isDark, toggle } = useTheme();
  return (
    <button
      onClick={toggle}
      className="p-2 rounded-lg text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      aria-label="Toggle theme"
    >
      {isDark ? <Sun size={17} /> : <Moon size={17} />}
    </button>
  );
}

// ─── Invoice type card ────────────────────────────────────────────────────────

function InvoiceTypeCard({ type, isNew = false }) {
  const IconComponent = iconMap[type.icon] || FileText;
  const cfg = typeConfig[type.id] || typeConfig.freelancer;

  return (
    <div className={`
      group relative bg-white dark:bg-gray-900/80 rounded-2xl border
      ${cfg.light} p-6 flex flex-col
      transition-all duration-300
      hover:shadow-2xl hover:shadow-black/8 hover:-translate-y-1.5
      hover:ring-4 ring-transparent ${cfg.ring}
    `}>
      {/* Gradient top bar */}
      <div className={`absolute top-0 left-0 right-0 h-[3px] rounded-t-2xl bg-gradient-to-r ${cfg.gradient} opacity-70 group-hover:opacity-100 transition-opacity`} />

      {/* New badge */}
      {isNew && (
        <span className={`absolute top-4 right-4 px-2 py-0.5 ${cfg.badge} text-[10px] font-bold rounded-full uppercase tracking-wider`}>
          New
        </span>
      )}

      {/* Icon */}
      <div className={`w-11 h-11 rounded-xl ${cfg.iconBg} flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110`}>
        <IconComponent className={`w-5 h-5 ${cfg.icon}`} />
      </div>

      <h3 className="text-[15px] font-semibold text-gray-900 dark:text-white mb-1.5 leading-snug">
        {type.name}
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-5 leading-relaxed flex-1">
        {type.description}
      </p>

      <ul className="space-y-1.5 mb-6">
        {(type.features || []).map((feature, i) => (
          <li key={i} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
            <div className={`w-1.5 h-1.5 rounded-full ${cfg.dot} flex-shrink-0`} />
            {feature}
          </li>
        ))}
      </ul>

      <Link
        to={`/create/${type.id}`}
        className={`
          inline-flex items-center justify-center gap-2 w-full px-4 py-2.5
          ${cfg.btn} text-white text-sm font-semibold rounded-xl shadow-md
          transition-all duration-200
        `}
      >
        Create Invoice
        <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
      </Link>
    </div>
  );
}

// ─── Stat ─────────────────────────────────────────────────────────────────────

function Stat({ value, label }) {
  return (
    <div className="text-center">
      <div className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{value}</div>
      <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">{label}</div>
    </div>
  );
}

// ─── Feature item ─────────────────────────────────────────────────────────────

function FeatureItem({ icon: Icon, title, description, accent }) {
  return (
    <div className="flex gap-4">
      <div className={`flex-shrink-0 w-10 h-10 rounded-xl ${accent} flex items-center justify-center shadow-sm`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <h3 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">{title}</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

// ─── Pricing card ─────────────────────────────────────────────────────────────

function PricingCard({ plan, price, period, description, features, locked, cta, ctaLink, onCta, highlight }) {
  return (
    <div className={`
      relative rounded-2xl p-8 flex flex-col
      ${highlight
        ? 'bg-gradient-to-b from-blue-600 to-indigo-700 text-white shadow-2xl shadow-blue-600/30'
        : 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800'}
    `}>
      {highlight && (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-3 py-1 bg-amber-400 text-amber-900 text-xs font-bold rounded-full uppercase tracking-wide shadow-sm">
          Most Popular
        </div>
      )}

      <div className="mb-6">
        <h3 className={`text-lg font-bold mb-1 ${highlight ? 'text-white' : 'text-gray-900 dark:text-white'}`}>{plan}</h3>
        <p className={`text-sm ${highlight ? 'text-blue-200' : 'text-gray-500 dark:text-gray-400'}`}>{description}</p>
      </div>

      <div className="mb-8 flex items-end gap-1">
        <span className={`text-4xl font-extrabold ${highlight ? 'text-white' : 'text-gray-900 dark:text-white'}`}>{price}</span>
        {period && <span className={`text-sm pb-1 ${highlight ? 'text-blue-200' : 'text-gray-500 dark:text-gray-400'}`}>{period}</span>}
      </div>

      <ul className="space-y-3 mb-8 flex-1">
        {features.map((f, i) => (
          <li key={i} className={`flex items-start gap-3 text-sm ${highlight ? 'text-blue-50' : 'text-gray-600 dark:text-gray-300'}`}>
            {f.included
              ? <CheckCircle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${highlight ? 'text-blue-200' : 'text-emerald-500'}`} />
              : <Lock className={`w-4 h-4 flex-shrink-0 mt-0.5 ${highlight ? 'text-blue-400/60' : 'text-gray-300 dark:text-gray-600'}`} />
            }
            <span className={!f.included ? (highlight ? 'text-blue-400/60' : 'text-gray-400 dark:text-gray-600') : ''}>
              {f.text}
            </span>
          </li>
        ))}
      </ul>

      {ctaLink ? (
        <Link
          to={ctaLink}
          className={`
            block w-full text-center px-6 py-3 rounded-xl font-semibold text-sm transition-all
            ${highlight
              ? 'bg-white text-blue-600 hover:bg-blue-50 shadow-md'
              : 'border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}
          `}
        >
          {cta}
        </Link>
      ) : (
        <button
          onClick={onCta}
          disabled={locked && !onCta}
          className={`
            w-full px-6 py-3 rounded-xl font-semibold text-sm transition-all
            ${highlight
              ? 'bg-white text-blue-600 hover:bg-blue-50 shadow-md'
              : locked && !onCta
                ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : 'border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}
          `}
        >
          {cta}
        </button>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function HomePage() {
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState({ open: false, reason: '' });
  const [stats, setStats] = useState(null);
  const { isFeatureEnabled, isProUser, monthlyUsed, monthlyLimit, refresh } = useEntitlements();
  const { user, signOut } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const justUpgraded = searchParams.get('upgraded') === 'true';
  const pendingCheckout = searchParams.get('checkout') === '1';
  const mobileMenuRef = useRef(null);
  const canViewHistory = isFeatureEnabled(Features.INVOICE_HISTORY);

  useEffect(() => {
    let mounted = true;
    fetchAllInvoiceTypes()
      .then(types => { if (mounted && types) setInvoiceTypes(types); })
      .catch(() => { if (mounted) setInvoiceTypes(getLocalInvoiceTypes()); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  // Fetch stats separately — re-runs when user logs in or plan changes
  useEffect(() => {
    if (!user || !canViewHistory) return;
    let mounted = true;
    getInvoiceStats().then(s => { if (mounted) setStats(s); }).catch(() => {});
    return () => { mounted = false; };
  }, [user, isProUser, canViewHistory]);

  // Poll entitlements for up to 30s (10 × 3s) after checkout redirect — webhook can lag
  useEffect(() => {
    if (!justUpgraded) return;
    let cancelled = false;
    let count = 0;
    const doPoll = async () => {
      if (cancelled) return;
      count++;
      await refresh();
      if (count < 10 && !cancelled) setTimeout(doPoll, 3000);
      else if (!cancelled) setSearchParams({}, { replace: true });
    };
    doPoll();
    return () => { cancelled = true; };
  }, [justUpgraded]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear ?upgraded=true as soon as the plan flips to Pro (no need to wait the full 30s)
  useEffect(() => {
    if (justUpgraded && isProUser) setSearchParams({}, { replace: true });
  }, [isProUser, justUpgraded, setSearchParams]);

  // After login-then-upgrade redirect: open the upgrade modal with the stored plan
  useEffect(() => {
    if (!pendingCheckout || !user) return;
    setSearchParams({}, { replace: true });
    const plan = sessionStorage.getItem('pendingUpgradePlan') || 'monthly';
    sessionStorage.removeItem('pendingUpgradePlan');
    setUpgradeModal({ open: true, reason: '', initialBillingCycle: plan });
  }, [pendingCheckout, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close mobile menu on outside click
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const handler = (e) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [mobileMenuOpen]);

  // First 3 cards in top row, remaining in bottom row (centered)
  const topRow = invoiceTypes.slice(0, 3);
  const bottomRow = invoiceTypes.slice(3);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 text-gray-900 dark:text-white">
      <SEO
        title="Invilic: Free Professional Invoice Generator for Freelancers & Businesses"
        description="Create GST, VAT, and standard invoices in seconds. Free invoice generator for freelancers, coaches, and businesses worldwide. Download PDF, email clients, and track payments."
        canonical="https://www.invilic.com/"
        schema={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'WebSite',
              'name': 'Invilic',
              'url': 'https://www.invilic.com',
              'description': 'Free professional invoice generator for freelancers and businesses. Create GST, VAT, and international invoices in seconds.',
              'potentialAction': {
                '@type': 'SearchAction',
                'target': { '@type': 'EntryPoint', 'urlTemplate': 'https://www.invilic.com/blog?q={search_term_string}' },
                'query-input': 'required name=search_term_string',
              },
            },
            {
              '@type': 'Organization',
              'name': 'Invilic',
              'url': 'https://www.invilic.com',
              'logo': 'https://www.invilic.com/3.png',
              'description': 'Free invoice generator for freelancers, coaches, and businesses. Supports GST (India), VAT (UK/EU/Australia), and international invoices.',
              'founder': { '@type': 'Person', 'name': 'Navjot Singh' },
              'foundingDate': '2024',
              'areaServed': 'Worldwide',
              'knowsAbout': ['Invoice generation', 'GST invoices', 'VAT invoices', 'Freelancer billing', 'Small business invoicing'],
              'sameAs': [],
            },
          ],
        }}
      />

      {/* ── Navbar ── */}
      <nav className="sticky top-0 z-50 bg-white/90 dark:bg-gray-950/90 backdrop-blur-xl border-b border-gray-100 dark:border-gray-800/60" ref={mobileMenuRef}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/*
            True 3-column layout:
              flex-1 [logo left] | [center links] | flex-1 justify-end [actions right]
            Both outer columns are flex-1 so center is always perfectly centred.
          */}
          <div className="flex items-center h-16">

            {/* ── Col 1: Logo ── */}
            <div className="flex-1 flex items-center">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center group"
              >
                <img
                  src="/2.png"
                  alt="Invilic"
                  className="h-8 w-auto transition-opacity group-hover:opacity-70"
                />
              </Link>
            </div>

            {/* ── Col 2: Centre nav (guests only, hidden on mobile) ── */}
            {!user && (
              <div className="hidden md:flex items-center gap-1">
                <a
                  href="#features"
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all"
                >
                  Features
                </a>
                <a
                  href="#pricing"
                  className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-all"
                >
                  Pricing
                </a>
              </div>
            )}

            {/* ── Col 3: Right actions ── */}
            <div className="flex-1 flex items-center justify-end gap-2">
              <ThemeToggle />

              {user ? (
                <>
                  {/* Plan badge */}
                  {isProUser ? (
                    <Link
                      to="/billing"
                      className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 border border-green-200 dark:border-green-800 rounded-lg transition-colors"
                    >
                      <Zap className="w-3.5 h-3.5" /> Pro
                    </Link>
                  ) : (
                    <button
                      onClick={() => setUpgradeModal({ open: true, reason: `You've used ${monthlyUsed} of ${monthlyLimit} free invoices this month.` })}
                      className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 border border-blue-200 dark:border-blue-800 rounded-lg transition-colors"
                    >
                      <Zap className="w-3.5 h-3.5" /> Upgrade
                    </button>
                  )}

                  {/* Avatar */}
                  <div
                    title={user.email}
                    className="hidden sm:flex w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 items-center justify-center text-white text-sm font-bold select-none cursor-default shadow-md shadow-blue-500/25 flex-shrink-0"
                  >
                    {user.email.charAt(0).toUpperCase()}
                  </div>

                  {/* Sign out — icon always, label on lg+ */}
                  <button
                    onClick={signOut}
                    title="Sign out"
                    className="hidden sm:flex items-center gap-1.5 text-sm text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 px-2 py-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                  >
                    <LogOut className="w-4 h-4 flex-shrink-0" />
                    <span className="font-medium hidden lg:inline">Sign out</span>
                  </button>

                  {/* Mobile hamburger */}
                  <button
                    onClick={() => setMobileMenuOpen(v => !v)}
                    className="sm:hidden p-2 rounded-lg text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    aria-label="Menu"
                  >
                    {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/auth"
                    className="hidden sm:block text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white px-3 py-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/auth"
                    className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-semibold rounded-xl transition-all shadow-sm shadow-blue-500/25 hover:shadow-md hover:shadow-blue-500/30 hover:-translate-y-px whitespace-nowrap"
                  >
                    <span className="hidden sm:inline">Get started free</span>
                    <span className="sm:hidden">Start free</span>
                  </Link>
                  {/* Mobile hamburger */}
                  <button
                    onClick={() => setMobileMenuOpen(v => !v)}
                    className="md:hidden p-2 rounded-lg text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    aria-label="Menu"
                  >
                    {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                  </button>
                </>
              )}
            </div>

          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-950 px-4 py-3 space-y-1">
            {!user ? (
              <>
                <a
                  href="#features"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <Zap className="w-4 h-4 text-gray-400" />
                  Features
                </a>
                <a
                  href="#pricing"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <Star className="w-4 h-4 text-gray-400" />
                  Pricing
                </a>
                <Link
                  to="/auth"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 rounded-lg transition-colors"
                >
                  Sign in
                </Link>
              </>
            ) : (
              <>
                {/* History — locked for free users */}
                {isFeatureEnabled(Features.INVOICE_HISTORY) ? (
                  <Link
                    to="/history"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                  >
                    <Clock className="w-4 h-4 text-gray-400" />
                    Invoice History
                  </Link>
                ) : (
                  <button
                    onClick={() => { setMobileMenuOpen(false); setUpgradeModal({ open: true, reason: 'Invoice history is a Pro feature. Upgrade to access your full invoice records.' }); }}
                    className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium text-gray-400 dark:text-gray-500 hover:bg-amber-50 dark:hover:bg-amber-950/20 rounded-lg transition-colors"
                  >
                    <Lock className="w-4 h-4 text-gray-300 dark:text-gray-600" />
                    Invoice History
                    <span className="ml-auto text-[10px] font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-1.5 py-0.5 rounded-full">Pro</span>
                  </button>
                )}
                <Link
                  to="/clients"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <Users className="w-4 h-4 text-gray-400" />
                  Client Address Book
                </Link>
                <Link
                  to="/profile"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <Building2 className="w-4 h-4 text-gray-400" />
                  Business Profiles
                </Link>
                <Link
                  to="/settings"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <Sparkles className="w-4 h-4 text-gray-400" />
                  Settings
                </Link>

                <div className="h-px bg-gray-100 dark:bg-gray-800 my-1" />

                {isProUser ? (
                  <Link
                    to="/billing"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-950/30 rounded-lg transition-colors"
                  >
                    <Zap className="w-4 h-4" />
                    Pro: Manage Plan
                  </Link>
                ) : (
                  <button
                    onClick={() => { setMobileMenuOpen(false); setUpgradeModal({ open: true, reason: '' }); }}
                    className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 rounded-lg transition-colors"
                  >
                    <Zap className="w-4 h-4" />
                    Upgrade to Pro
                  </button>
                )}

                <div className="px-3 py-2 text-xs text-gray-400 dark:text-gray-500 truncate">{user.email}</div>
                <button
                  onClick={() => { signOut(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Sign out
                </button>
              </>
            )}
          </div>
        )}
      </nav>

      {user ? (
        /* ── Dashboard (logged-in) ── */
        <section className="px-4 sm:px-6 lg:px-8 pt-10 pb-4">
          <div className="max-w-6xl mx-auto space-y-8">

            {/* Post-payment success banner */}
            {justUpgraded && (
              <div className="flex items-start gap-3 px-4 py-3.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-green-800 dark:text-green-300">Payment successful. Welcome to Pro!</p>
                  <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">All features are now unlocked. If your plan badge hasn't updated yet, wait a moment and refresh.</p>
                </div>
              </div>
            )}

            {/* Greeting row */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Welcome back, {user.email.split('@')[0]} 👋
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {new Date().toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              </div>
              <Link
                to="/create/freelancer"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-md shadow-blue-600/20 flex-shrink-0"
              >
                <Sparkles className="w-4 h-4" />
                New invoice
              </Link>
            </div>

            {/* Stats row — only for Pro (history access) */}
            {stats && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[
                  { label: 'This month',  value: stats.this_month_count, color: 'text-blue-600 dark:text-blue-400'   },
                  { label: 'Unpaid',      value: stats.unpaid_count,     color: 'text-amber-600 dark:text-amber-400' },
                  { label: 'Overdue',     value: stats.overdue_count,    color: 'text-red-600 dark:text-red-400'     },
                  { label: 'Total',       value: stats.total_invoices,   color: 'text-gray-700 dark:text-gray-300'   },
                ].map(s => (
                  <div key={s.label} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl px-5 py-4">
                    <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Quick links */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { to: '/history',  icon: Clock,     label: 'History',  locked: !isFeatureEnabled(Features.INVOICE_HISTORY) },
                { to: '/clients',  icon: Users,     label: 'Clients',  locked: false },
                { to: '/profile',  icon: Building2, label: 'Profiles', locked: false },
                { to: '/settings', icon: Sparkles,  label: 'Settings', locked: false },
              ].map(({ to, icon: Icon, label, locked }) =>
                locked ? (
                  <button
                    key={label}
                    onClick={() => setUpgradeModal({ open: true, reason: 'Invoice history is a Pro feature. Upgrade to access your full invoice records.' })}
                    className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl text-sm font-medium text-gray-400 dark:text-gray-500 hover:border-amber-300 dark:hover:border-amber-700 transition-colors"
                  >
                    <Lock className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" />
                    {label}
                    <span className="ml-auto text-[10px] font-bold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-1.5 py-0.5 rounded-full flex-shrink-0">Pro</span>
                  </button>
                ) : (
                  <Link
                    key={to}
                    to={to}
                    className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:border-blue-300 dark:hover:border-blue-700 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                  >
                    <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    {label}
                  </Link>
                )
              )}
            </div>

          </div>
        </section>
      ) : (
        /* ── Hero (logged-out) ── */
        <section className="relative pt-20 pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50/80 via-white to-indigo-50/50 dark:from-blue-950/20 dark:via-gray-950 dark:to-indigo-950/20 pointer-events-none" />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-300/40 dark:via-blue-700/30 to-transparent" />
          <div className="absolute -top-20 left-1/3 w-96 h-96 bg-blue-200/25 dark:bg-blue-900/15 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute top-10 right-1/4 w-80 h-80 bg-indigo-200/20 dark:bg-indigo-900/10 rounded-full blur-3xl pointer-events-none" />

          <div className="max-w-4xl mx-auto text-center relative">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-8 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700/80 rounded-full text-sm text-gray-600 dark:text-gray-300 font-medium shadow-sm">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              No account needed to get started
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-gray-900 dark:text-white leading-[1.06] tracking-tight mb-6">
              Professional invoices
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-blue-600 to-indigo-600 dark:from-blue-400 dark:via-blue-400 dark:to-indigo-400">
                ready in 60 seconds
              </span>
            </h1>
            <p className="text-xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              GST, VAT, and standard invoices for businesses anywhere in the world.
              Fill in your details, download a print-ready PDF.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-10">
              <Link
                to="/create/freelancer"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-blue-600/30 hover:shadow-xl hover:shadow-blue-600/40 hover:-translate-y-0.5"
              >
                <Sparkles className="w-4 h-4" />
                Create free invoice
              </Link>
              <a
                href="#types"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 font-semibold rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-all hover:-translate-y-0.5"
              >
                Browse templates
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-7 gap-y-2 text-sm text-gray-500 dark:text-gray-400 mb-16">
              {['No credit card required', 'No account needed', 'Instant PDF download'].map(t => (
                <span key={t} className="flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  {t}
                </span>
              ))}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8 border-t border-gray-100 dark:border-gray-800">
              <Stat value="10,000+" label="Invoices generated" />
              <Stat value="5" label="Invoice templates" />
              <Stat value="14+" label="Currencies supported" />
              <Stat value="VAT & GST" label="Tax ready" />
            </div>
          </div>
        </section>
      )}

      {/* ── Free plan usage banner ── */}
      {user && !isProUser && (
        <div className="px-4 sm:px-6 lg:px-8 -mb-6 mt-4">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between gap-4 px-4 py-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/60 rounded-xl">
              <div className="flex items-center gap-3 min-w-0">
                <Zap className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                <span className="text-sm text-blue-800 dark:text-blue-300 font-medium truncate">
                  Free plan: {monthlyUsed} of {monthlyLimit} invoices used this month
                </span>
                <div className="hidden sm:flex items-center gap-1 flex-shrink-0">
                  {Array.from({ length: monthlyLimit }).map((_, i) => (
                    <div
                      key={i}
                      className={`w-2.5 h-2.5 rounded-full ${i < monthlyUsed ? 'bg-blue-500' : 'bg-blue-200 dark:bg-blue-800'}`}
                    />
                  ))}
                </div>
              </div>
              <button
                onClick={() => setUpgradeModal({ open: true, reason: `You've used ${monthlyUsed} of ${monthlyLimit} free invoices this month.` })}
                className="flex-shrink-0 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 underline underline-offset-2 transition-colors"
              >
                Upgrade for unlimited
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Invoice type cards ── */}
      <section id="types" className={`px-4 sm:px-6 lg:px-8 ${user ? 'pt-8 pb-16' : 'py-20'}`}>
        <div className="max-w-6xl mx-auto">

          <div className={`${user ? 'mb-8' : 'text-center mb-12'}`}>
            {user ? (
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Create an invoice
              </h2>
            ) : (
              <>
                <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-3">Templates</p>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                  Pick the right invoice type
                </h2>
                <p className="text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
                  Each template ships with the correct fields, tax rules, and layout for your situation.
                </p>
              </>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20 gap-3 text-gray-500 dark:text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Loading templates...</span>
            </div>
          ) : (
            <>
              {/* Top row: first 3 */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {topRow.map(type => (
                  <InvoiceTypeCard key={type.id} type={type} isNew={type.id === 'gst'} />
                ))}
              </div>
              {/* Bottom row: remaining cards, centered */}
              {bottomRow.length > 0 && (
                <div className={`mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5 ${bottomRow.length < 3 ? 'lg:w-2/3 lg:mx-auto' : 'lg:grid-cols-3'}`}>
                  {bottomRow.map(type => (
                    <InvoiceTypeCard key={type.id} type={type} isNew={type.id === 'vat' || type.id === 'gst'} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ── Features — guests only ── */}
      {!user && <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50/70 dark:bg-gray-900/40">
        <div className="max-w-6xl mx-auto">

          <div className="text-center mb-14">
            <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-3">Why Invilic</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Everything you need to get paid faster
            </h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-xl mx-auto">
              Designed for real businesses, not spreadsheet workarounds.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            <FeatureItem
              icon={Zap}
              accent="bg-amber-500"
              title="Instant PDF generation"
              description="Download a print-ready PDF the moment you fill in your details. No processing, no queue."
            />
            <FeatureItem
              icon={Shield}
              accent="bg-emerald-500"
              title="Tax compliance built in"
              description="Auto-calculates GST (CGST/SGST/IGST) for India and VAT with reverse charge for businesses worldwide."
            />
            <FeatureItem
              icon={Globe}
              accent="bg-blue-500"
              title="Multi-currency support"
              description="Bill in USD, EUR, GBP, INR, and 10 more currencies with automatic symbol formatting."
            />
            <FeatureItem
              icon={BarChart2}
              accent="bg-violet-500"
              title="Invoice history"
              description="Every finalized invoice is stored with a searchable, downloadable record you can access anytime."
            />
            <FeatureItem
              icon={Building2}
              accent="bg-rose-500"
              title="Business branding"
              description="Upload your logo, enter your business details once, and every invoice goes out on your letterhead."
            />
            <FeatureItem
              icon={TrendingUp}
              accent="bg-indigo-500"
              title="Auto-save drafts"
              description="Your work is saved automatically as you type. Come back later and pick up exactly where you left off."
            />
          </div>
        </div>
      </section>}

      {/* ── Pricing ── */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">

          {isProUser ? (
            /* Pro users — show status instead of pricing cards */
            <div className="max-w-md mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-full text-green-700 dark:text-green-400 text-sm font-semibold mb-6">
                <Zap className="w-4 h-4" />
                You're on Pro
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                All features unlocked
              </h2>
              <p className="text-gray-500 dark:text-gray-400 mb-8">
                You have full access to unlimited invoices, history, email, sharing, and exports.
              </p>
              <Link
                to="/billing"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-semibold rounded-xl hover:opacity-90 transition-opacity"
              >
                Manage subscription
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-14">
                <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-3">Pricing</p>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                  Start free, scale when ready
                </h2>
                <p className="text-gray-500 dark:text-gray-400">
                  No credit card required to create your first invoice.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start max-w-2xl mx-auto">
                <PricingCard
                  plan="Free"
                  price="$0"
                  description="Perfect for occasional use"
                  ctaLink="/create/freelancer"
                  cta="Start for free"
                  features={[
                    { text: '5 invoices per month', included: true },
                    { text: 'All invoice templates', included: true },
                    { text: 'PDF download', included: true },
                    { text: 'Invoice history', included: false },
                    { text: 'Email invoices to clients', included: false },
                    { text: 'Shareable invoice links', included: false },
                    { text: 'CSV export & bulk download', included: false },
                  ]}
                />
                <PricingCard
                  plan="Pro"
                  price="$6"
                  period="/month"
                  description="For freelancers and small businesses"
                  highlight
                  cta="Upgrade to Pro"
                  onCta={() => setUpgradeModal({ open: true, reason: '' })}
                  features={[
                    { text: 'Unlimited invoices', included: true },
                    { text: 'Full invoice history & records', included: true },
                    { text: 'Email invoices to clients', included: true },
                    { text: 'Shareable invoice links', included: true },
                    { text: 'CSV export & bulk PDF download', included: true },
                    { text: 'Priority support', included: true },
                  ]}
                />
              </div>

              <div className="mt-10 text-center">
                <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                  $6/month · $60/year · Cancel anytime
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-gray-100 dark:border-gray-800 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center">
            <img
              src="/2.png"
              alt="Invilic"
              className="h-14 w-auto"
            />
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500">
            © {new Date().getFullYear()} Invilic. All rights reserved.
          </p>
          <div className="flex items-center gap-5 text-sm text-gray-400 dark:text-gray-500">
            <a href="#features" className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors">Features</a>
            <a href="#pricing" className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors">Pricing</a>
            {!user && <Link to="/auth" className="hover:text-gray-700 dark:hover:text-gray-300 transition-colors">Sign in</Link>}
          </div>
        </div>
      </footer>

      {upgradeModal.open && (
        <UpgradeModal
          reason={upgradeModal.reason}
          initialBillingCycle={upgradeModal.initialBillingCycle}
          onClose={() => setUpgradeModal({ open: false, reason: '' })}
        />
      )}
    </div>
  );
}
